import i18next from 'i18next';

export const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    return await Notification.requestPermission();
  }
  return Notification.permission;
};

// Set up service worker message listener for notification clicks
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data.type === 'notification-click') {
      if (event.data.notificationType === 'chat-message') {
        if (window.__chatNotificationClickHandler) {
          window.__chatNotificationClickHandler();
        }
      } else if (event.data.notificationType === 'video-call') {
        if (window.__videoCallNotificationClickHandler) {
          window.__videoCallNotificationClickHandler();
        }
      } else if (event.data.notificationType === 'email-message') {
        if (window.__emailNotificationClickHandler) {
          window.__emailNotificationClickHandler();
        }
      }
    }
  });
}

export const showChatNotification = async (message, onNotificationClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__chatNotificationClickHandler = onNotificationClick;

          await registration.showNotification('Lixani', {
            body: `${message.senderFirstName}: ${message.message}`,
            icon: '/favicon.ico',
            tag: 'chat-message',
            requireInteraction: true,
            vibrate: [200, 100, 200],
            data: {
              type: 'chat-message',
              threadId: message.threadId,
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification('Lixani', {
        body: `${message.senderFirstName}: ${message.message}`,
        icon: '/favicon.ico',
        tag: 'chat-message',
        requireInteraction: true,
      });

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};

export const showVideoCallNotification = async (body, onNotificationClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__videoCallNotificationClickHandler = onNotificationClick;

          await registration.showNotification('Lixani', {
            body,
            icon: '/favicon.ico',
            tag: 'video-call',
            requireInteraction: true,
            vibrate: [300, 100, 300, 100, 300],
            data: {
              type: 'video-call',
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification('Lixani', {
        body,
        icon: '/favicon.ico',
        tag: 'video-call',
        requireInteraction: true,
      });

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};

export const showEmailNotification = async (
  emailMessage,
  onNotificationClick
) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    // Parse from address - it's an object with an 'address' property
    const fromAddress =
      emailMessage.from?.address ||
      i18next.t('projectEmail.from', 'Unknown sender');
    const subject =
      emailMessage.subject || i18next.t('projectEmail.subject', 'No subject');
    const title = i18next.t(
      'projectEmail.new_email_notification',
      'New Email - Lixani'
    );
    const bodyText = `${i18next.t('projectEmail.from', 'From')}: ${fromAddress}\n${subject}`;

    // Try Service Worker API first (required for mobile)
    if ('serviceWorker' in navigator) {
      try {
        // Get existing registration
        const registration = await navigator.serviceWorker.getRegistration();

        // If we have a registration with an active worker, show notification
        if (registration && registration.active) {
          // Store click handler globally for service worker notifications
          window.__emailNotificationClickHandler = onNotificationClick;

          await registration.showNotification(title, {
            body: bodyText,
            icon: '/favicon.ico',
            tag: 'email-message',
            requireInteraction: false,
            vibrate: [200, 100, 200],
            data: {
              type: 'email-message',
              emailId: emailMessage.id,
              projectKey: emailMessage.projectKey,
              url: window.location.origin,
            },
          });
          return;
        }
      } catch (swError) {
        // Service Worker failed, try fallback
      }
    }

    // Desktop fallback - try regular Notification API
    try {
      const notification = new Notification(title, {
        body: bodyText,
        icon: '/favicon.ico',
        tag: 'email-message',
        requireInteraction: false,
      });

      notification.onclick = () => {
        if (onNotificationClick) {
          onNotificationClick();
        }
        window.focus();
        notification.close();
      };
    } catch (constructorError) {
      // Silently ignore if Notification constructor fails
    }
  } catch (error) {
    // Silently ignore all notification errors
  }
};
